import i18n from '@/utils/i18n';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { ConfigurableColumn } from '@experiences/components/table/blocs/utils/configurable-columns-utils';
import { ConfigurableColumnDef } from '@experiences/components/table/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnRow } from '@experiences/components/table/configure-column-row.web';
import { createStyles } from '@/contexts/theme-context';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { Dropdown } from '@usepepper/ui/atoms/dropdown';
import { IconHolder, Icons, Tooltip } from '@usepepper/sprout-kit-ui';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';

const DROPDOWN_MIN_WIDTH = 230;
const DROPDOWN_MAX_WIDTH = 300;

// React Native Web's Modal uses z-index 9999; the drag clone must render above it.
const DRAG_CLONE_Z_INDEX = 10_000;

// Vertical space reserved for content above/below the dropdown (header, trigger, margin).
const DROPDOWN_VERTICAL_MARGIN = 260;

interface ConfigureColumnsButtonProps {
  /** Column definitions for the table. Must include `meta.hideable` on each configurable column. */
  columns: ConfigurableColumnDef[];
  domain: ConfigurationDomain;
  table: TableName;
}

/**
 * Trigger button + popover that lets users toggle column visibility and
 * drag-to-reorder columns. Reads configuration from each column's
 * `meta.hideable / alwaysVisible / label`; preferences are persisted across
 * sessions, scoped by `domain` + `table`.
 *
 * Uses @hello-pangea/dnd's `renderClone` prop to portal dragged items to
 * document.body, avoiding clipping by the Dropdown's Modal stacking context.
 */
export const ConfigureColumnsButton: React.FC<ConfigureColumnsButtonProps> =
  memo(({ columns, domain, table }) => {
    const [styles, theme] = useStyles();
    const [isOpen, setIsOpen] = useState(false);
    const { height: windowHeight } = useWindowDimensions();
    const scrollableMaxHeight = windowHeight - DROPDOWN_VERTICAL_MARGIN;

    const {
      columnVisibility,
      configurableColumns,
      configurableColumnsOrder,
      reorderColumns,
      resetToDefaults,
      toggleColumnVisibility,
    } = useConfigureColumns(domain, table, columns);

    const orderedColumns = useMemo(() => {
      const byId = new Map(configurableColumns.map((c) => [c.id, c]));
      return configurableColumnsOrder
        .map((id) => byId.get(id))
        .filter((column): column is ConfigurableColumn => Boolean(column));
    }, [configurableColumns, configurableColumnsOrder]);

    const handleDragEnd = useCallback(
      (result: DropResult) => {
        if (
          !result.destination ||
          result.destination.index === result.source.index
        ) {
          return;
        }

        const reordered = [...orderedColumns];
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        reorderColumns(reordered.map((c) => c.id));
      },
      [orderedColumns, reorderColumns],
    );

    const handleClose = useCallback(() => setIsOpen(false), []);

    const isColumnVisible = useCallback(
      (columnId: string) => columnVisibility[columnId] !== false,
      [columnVisibility],
    );

    const renderItem = useCallback(
      (column: ConfigurableColumn, index: number) => (
        <Draggable draggableId={column.id} index={index} key={column.id}>
          {(draggableProvided, snapshot) => (
            <div
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
            >
              <ConfigureColumnRow
                columnId={column.id}
                dragHandleProps={draggableProvided.dragHandleProps}
                isDragging={snapshot.isDragging}
                isVisible={isColumnVisible(column.id)}
                label={column.label}
                onToggle={toggleColumnVisibility}
              />
            </div>
          )}
        </Draggable>
      ),
      [isColumnVisible, toggleColumnVisibility],
    );

    return (
      <View style={styles.triggerWrapper}>
        <Dropdown
          containerStyle={styles.dropdownContainer}
          isVisible={isOpen}
          onClose={handleClose}
          position="bottom-start"
          trigger={
            <Tooltip offset={4} placement="bottom">
              <Tooltip.Trigger>
                <Pressable
                  accessibilityLabel={i18n.t(
                    'sales_portal.table.column_preferences.tooltip',
                  )}
                  accessibilityRole="button"
                  onPress={() => setIsOpen((prev) => !prev)}
                  style={({ pressed, hovered }) =>
                    StyleSheet.flatten([
                      styles.trigger,
                      (pressed || isOpen || hovered) && styles.triggerActive,
                    ])
                  }
                >
                  <IconHolder color={theme.sk.color.icon.secondary} size="$sm">
                    <Icons.Columns3 />
                  </IconHolder>
                </Pressable>
              </Tooltip.Trigger>
              <Tooltip.Content>
                <Tooltip.Text>
                  {i18n.t('sales_portal.table.column_preferences.tooltip')}
                </Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          }
        >
          <View style={styles.content}>
            <View style={styles.header}>
              <Typography
                color={theme.sk.color.text.primary}
                variant="label-sm"
              >
                {i18n.t('sales_portal.table.column_preferences.title')}
              </Typography>
              <Pressable
                onPress={resetToDefaults}
                style={({ pressed, hovered }) => [
                  styles.resetButton,
                  hovered
                    ? styles.resetButtonHovered
                    : pressed
                      ? styles.resetButtonPressed
                      : null,
                ]}
              >
                <Typography
                  color={theme.sk.color.text.secondary}
                  variant="paragraph-sm"
                >
                  {i18n.t('sales_portal.table.column_preferences.reset')}
                </Typography>
              </Pressable>
            </View>
            <ScrollView style={{ maxHeight: scrollableMaxHeight }}>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable
                  droppableId="configure-columns"
                  renderClone={(provided, snapshot, rubric) => {
                    const column = orderedColumns[rubric.source.index];
                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          zIndex: DRAG_CLONE_Z_INDEX,
                        }}
                      >
                        <ConfigureColumnRow
                          columnId={column.id}
                          dragHandleProps={provided.dragHandleProps}
                          isDragging={snapshot.isDragging}
                          isVisible={isColumnVisible(column.id)}
                          label={column.label}
                          onToggle={toggleColumnVisibility}
                        />
                      </div>
                    );
                  }}
                >
                  {(droppableProvided) => (
                    <div
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {orderedColumns.map(renderItem)}
                      {droppableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </ScrollView>
          </View>
        </Dropdown>
      </View>
    );
  });

const useStyles = createStyles((theme) => ({
  content: {
    width: '100%',
  },
  dropdownContainer: {
    maxWidth: DROPDOWN_MAX_WIDTH,
    minWidth: DROPDOWN_MIN_WIDTH,
    paddingLeft: theme.sk.spacing.spaceSMedium,
    paddingRight: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
  },
  resetButton: {
    borderRadius: theme.sk.radii.radiusSmall,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
  resetButtonHovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  resetButtonPressed: {
    opacity: 0.7,
  },
  trigger: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.button.secondary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    height: theme.sk.spacing.space2xlarge,
    justifyContent: 'center',
    width: theme.sk.spacing.space2xlarge,
  },
  triggerActive: {
    backgroundColor: theme.sk.color.bg.button.secondary.hover,
  },
  triggerWrapper: {
    alignSelf: 'center',
    marginLeft: theme.sk.spacing.spaceSmall,
  },
}));
